import { useMemo } from "react";
import { Button, EmptyState, Spinner } from "@cntrl/ui";
import { useNavigation } from "../../app/hooks/useSession.js";
import { useResolvedOrg, orgLensContext } from "../../app/hooks/useRoster.js";
import { BandProvider, bandContextFromNav } from "../../app/bandContext.js";
import { useLiveGroup } from "../../app/realtime/RealtimeProvider.js";
import styles from "./RosterShell.module.scss";

/**
 * The per-artist BandProvider for the roster lens — the "iframes die here" mount point. It
 * resolves the ORG-LENS navigation document for `(activeOrg, artistId)` (`org:{orgId}:band:{artistId}`)
 * and provides a `shell:'roster'` BandContext, so the SAME feature modules render under the org lens with the
 * server-computed effective role + capabilities (money-FREE for a Label subview, full for Management/Booking —
 * enforced server-side, never a client money gate). Joins the focused `band:{artistId}` SignalR group (leaves the
 * old one declaratively on artist change — the declarative half of the switchRosterArtist scorch).
 *
 * An unreachable artist (an org staffer with no assignment) 403s at the resolver — designed error, never a bare
 * mount. In practice the reach-scoped rail never lists an unreachable artist, so this is defense in depth.
 */
export function RosterBandProvider({ artistId, children }: { artistId: string; children: React.ReactNode }) {
  const { orgId } = useResolvedOrg();
  const nav = useNavigation(orgId ? orgLensContext(orgId, artistId) : undefined);

  // Join the focused artist's band group; leaves the previous artist's group when artistId changes.
  useLiveGroup(`band:${artistId}`);

  const bandContext = useMemo(
    () => (nav.data ? bandContextFromNav(nav.data, "roster") : null),
    [nav.data],
  );

  if (nav.isError) {
    // fetchNavigation throws an AuthError carrying the HTTP status; a 403 is the resolver's insufficient-reach.
    const status = (nav.error as { status?: number } | undefined)?.status;
    return (
      <EmptyState
        title={status === 403 ? "You don't have access to this artist" : "We couldn't load this artist"}
        description={
          status === 403
            ? "You're not assigned to this artist in your organization."
            : "Check your connection and try again."
        }
        action={status === 403 ? undefined : <Button onClick={() => nav.refetch()}>Retry</Button>}
      />
    );
  }

  if (!bandContext) {
    return (
      <div className={styles.contentLoading} role="status" aria-live="polite">
        <Spinner size={22} label="Loading this artist…" />
      </div>
    );
  }

  return <BandProvider value={bandContext}>{children}</BandProvider>;
}
